'use client';

import type { ReactNode } from 'react';
import { FormEvent, MouseEvent, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import {
  CalendarDays,
  Check,
  ChevronDown,
  Grid2X2,
  MapPin,
  MessageCircle,
  Search,
  SlidersHorizontal,
  Star,
  X,
} from 'lucide-react';
import type { BusinessCategoryOption } from '@/lib/business-taxonomy';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

export interface DirectoryControlFilters {
  category: string;
  location: string;
  openToday: boolean;
  query: string;
  rating: boolean;
  sort: string;
  type: string;
  whatsapp: boolean;
}

interface ProfilesDirectoryControlsProps {
  categories: BusinessCategoryOption[];
  filters: DirectoryControlFilters;
  locations: string[];
}

interface DirectoryFilterLinkProps {
  children: ReactNode;
  className?: string;
  href: string;
}

interface SelectOption {
  label: string;
  value: string;
}

interface DropdownPosition {
  left: number;
  maxHeight: number;
  top: number;
  width: number;
}

function buildFilterHref(filters: DirectoryControlFilters, updates: Partial<DirectoryControlFilters>) {
  const next: DirectoryControlFilters = { ...filters, ...updates };
  const params = new URLSearchParams();

  if (next.query.trim()) params.set('q', next.query.trim());
  if (next.category) params.set('categoria', next.category);
  if (next.type) params.set('tipo', next.type);
  if (next.location) params.set('ubicacion', next.location);
  if (next.openToday) params.set('hoy', '1');
  if (next.whatsapp) params.set('whatsapp', '1');
  if (next.rating) params.set('valorados', '1');
  if (next.sort && next.sort !== 'recomendados') params.set('orden', next.sort);

  const query = params.toString();
  return `/perfiles/${query ? `?${query}` : ''}`;
}

function hasActiveMobileFilters(filters: DirectoryControlFilters): boolean {
  return Boolean(filters.location || filters.openToday || filters.whatsapp || filters.rating);
}

function OverlayPortal({ children }: { children: ReactNode }) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted) return null;

  return createPortal(children, document.body);
}

function PendingIndicator({ isPending }: { isPending: boolean }) {
  if (!isPending) return null;

  return (
    <OverlayPortal>
      <div className="pointer-events-none fixed inset-x-0 top-0 z-[130]">
        <div className="h-1 w-full overflow-hidden bg-accent/10">
          <div className="h-full w-full animate-pulse rounded-full bg-accent shadow-[0_0_18px_rgb(216_168_119/0.7)]" />
        </div>
        <div className="mx-auto mt-3 flex w-fit items-center gap-2 rounded-button border border-accent/30 bg-bg-secondary/95 px-4 py-2 text-sm font-semibold text-text-primary shadow-[0_18px_50px_rgb(0_0_0/0.35)] backdrop-blur">
          <span className="h-2 w-2 animate-pulse rounded-full bg-accent" />
          Actualizando resultados
        </div>
      </div>
    </OverlayPortal>
  );
}

export function DirectoryFilterLink({ children, className, href }: DirectoryFilterLinkProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }

    event.preventDefault();
    startTransition(() => {
      router.push(href);
    });
  };

  return (
    <>
      <PendingIndicator isPending={isPending} />
      <a href={href} onClick={handleClick} className={className}>
        {children}
      </a>
    </>
  );
}

function FilterButton({
  active,
  children,
  filters,
  updates,
}: {
  active: boolean;
  children: ReactNode;
  filters: DirectoryControlFilters;
  updates: Partial<DirectoryControlFilters>;
}) {
  return (
    <DirectoryFilterLink
      href={buildFilterHref(filters, updates)}
      className={cn(
        'inline-flex min-h-10 items-center gap-2 rounded-button border px-4 py-2 text-sm font-semibold transition-colors',
        active
          ? 'border-accent bg-accent text-bg-primary'
          : 'border-border-strong bg-bg-secondary/70 text-text-secondary hover:border-accent/70 hover:text-accent'
      )}
    >
      {children}
    </DirectoryFilterLink>
  );
}

function DropdownFilter({
  icon,
  label,
  onChange,
  options,
  value,
}: {
  icon: ReactNode;
  label: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  value: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const selected = options.find((option) => option.value === value);
  const displayLabel = selected?.label ?? label;
  const menuHeight = Math.min(280, Math.max(44, options.length * 44));

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportPadding = 12;
      const menuWidth = Math.max(224, rect.width);
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        window.innerWidth - menuWidth - viewportPadding
      );
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;
      const opensAbove = spaceBelow < Math.min(menuHeight, 180) && spaceAbove > spaceBelow;
      const top = opensAbove
        ? Math.max(viewportPadding, rect.top - menuHeight - 8)
        : Math.min(rect.bottom + 8, window.innerHeight - viewportPadding);
      const maxHeight = opensAbove
        ? Math.min(menuHeight, Math.max(120, spaceAbove - 8))
        : Math.min(menuHeight, Math.max(120, spaceBelow - 8));

      setPosition({ left, maxHeight, top, width: menuWidth });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, menuHeight]);

  return (
    <div className="relative min-w-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={cn(
          'inline-flex min-h-10 w-full min-w-0 items-center gap-2 rounded-button border px-4 py-2 text-left text-sm font-semibold transition-colors',
          value
            ? 'border-accent bg-accent/10 text-accent'
            : 'border-border-strong bg-bg-secondary/90 text-text-primary hover:border-accent/70'
        )}
        aria-expanded={isOpen}
      >
        <span className="shrink-0 text-accent">{icon}</span>
        <span className="min-w-0 flex-1 truncate">{displayLabel}</span>
        <ChevronDown size={15} className={cn('shrink-0 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && position ? (
        <OverlayPortal>
          <button
            type="button"
            aria-label="Cerrar opciones"
            className="fixed inset-0 z-[140] cursor-default bg-transparent"
            onClick={() => setIsOpen(false)}
          />
          <div
            className="fixed z-[150] overflow-y-auto rounded-lg border border-border-strong bg-bg-secondary text-text-primary shadow-[0_18px_60px_rgb(0_0_0/0.45)]"
            style={{
              left: position.left,
              maxHeight: position.maxHeight,
              top: position.top,
              width: position.width,
            }}
          >
            {options.map((option) => {
              const active = option.value === value;
              return (
                <button
                  key={option.value || 'all'}
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    onChange(option.value);
                  }}
                  className={cn(
                    'flex min-h-11 w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm transition-colors hover:bg-accent/12',
                    active ? 'bg-accent/14 text-accent' : 'text-text-primary'
                  )}
                >
                  <span className="truncate">{option.label}</span>
                  {active ? <Check size={15} /> : null}
                </button>
              );
            })}
          </div>
        </OverlayPortal>
      ) : null}
    </div>
  );
}

function MobileFilterSheet({
  filters,
  isOpen,
  locations,
  onApply,
  onClose,
}: {
  filters: DirectoryControlFilters;
  isOpen: boolean;
  locations: string[];
  onApply: (updates: Partial<DirectoryControlFilters>) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState({
    location: filters.location,
    openToday: filters.openToday,
    rating: filters.rating,
    whatsapp: filters.whatsapp,
  });

  useEffect(() => {
    if (!isOpen) return;

    setDraft({
      location: filters.location,
      openToday: filters.openToday,
      rating: filters.rating,
      whatsapp: filters.whatsapp,
    });
  }, [filters.location, filters.openToday, filters.rating, filters.whatsapp, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const filterItems = [
    {
      description: 'Negocios con horario visible para hoy.',
      icon: CalendarDays,
      key: 'openToday',
      label: 'Disponibilidad hoy',
    },
    {
      description: 'Perfiles con un número publicado para contacto por WhatsApp.',
      icon: MessageCircle,
      key: 'whatsapp',
      label: 'Contacto por WhatsApp',
    },
    {
      description: 'Calificacion publica desde 4.5.',
      icon: Star,
      key: 'rating',
      label: 'Mejor valorados',
    },
  ] as const;

  if (!isOpen) return null;

  return (
    <OverlayPortal>
      <div className="fixed inset-0 z-[120] md:hidden" role="dialog" aria-modal="true" aria-label="Filtros">
        <button
          type="button"
          aria-label="Cerrar filtros"
          className="absolute inset-0 bg-black/70"
          onClick={onClose}
        />
        <div className="absolute inset-x-0 bottom-0 max-h-[82vh] overflow-hidden rounded-t-[18px] border border-border bg-bg-primary text-text-primary shadow-[0_-24px_80px_rgb(0_0_0/0.45)]">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div>
              <p className="font-heading text-lg font-semibold">Filtros</p>
              <p className="mt-0.5 text-xs text-text-secondary">Refina los negocios disponibles</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-text-secondary"
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          </div>

        <div className="max-h-[calc(82vh-8.75rem)] overflow-y-auto px-5 py-4">
            <div className="space-y-6">
              <section>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="font-heading text-sm font-semibold text-text-primary">Ubicaciones disponibles</h3>
                </div>

                <div className="flex flex-wrap gap-2">
                  {locations.map((location) => {
                    const active = draft.location === location;
                    return (
                      <button
                        key={location}
                        type="button"
                        onClick={() => setDraft((current) => ({ ...current, location: active ? '' : location }))}
                        className={cn(
                          'rounded-button border px-3 py-2 text-xs font-semibold transition-colors',
                          active
                            ? 'border-accent bg-accent text-bg-primary'
                            : 'border-border-strong text-text-secondary'
                        )}
                      >
                        {location}
                      </button>
                    );
                  })}
                  {locations.length === 0 ? (
                    <p className="text-sm text-text-secondary">No hay ubicaciones publicadas todavía.</p>
                  ) : null}
                </div>
              </section>

              <section>
                <h3 className="mb-1 font-heading text-sm font-semibold text-text-primary">Filtros rapidos</h3>
                <div className="grid gap-2">
              {filterItems.map((item) => {
                const Icon = item.icon;
                const key = item.key;
                const active = draft[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setDraft((current) => ({ ...current, [key]: !current[key] }))}
                    className="flex min-h-[4.5rem] items-center gap-3 border-b border-border py-3 text-left last:border-b-0"
                  >
                    <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border', active ? 'border-accent bg-accent/12 text-accent' : 'border-border-strong text-text-secondary')}>
                      <Icon size={18} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-heading text-sm font-semibold text-text-primary">{item.label}</span>
                      <span className="mt-0.5 block text-xs leading-5 text-text-secondary">{item.description}</span>
                    </span>
                    <span className={cn('relative h-6 w-11 shrink-0 rounded-full border transition-colors', active ? 'border-accent bg-accent' : 'border-border-strong bg-bg-secondary')}>
                      <span className={cn('absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white transition-transform', active ? 'translate-x-5' : 'translate-x-1')} />
                    </span>
                  </button>
                );
              })}
                </div>
              </section>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 border-t border-border bg-bg-secondary/80 px-5 py-4">
            <button
              type="button"
              onClick={() => {
                setDraft({ location: '', openToday: false, rating: false, whatsapp: false });
                onApply({ location: '', openToday: false, rating: false, whatsapp: false });
                onClose();
              }}
              className="inline-flex min-h-11 items-center justify-center rounded-button border border-border-strong px-4 py-2 font-heading text-sm font-semibold text-text-secondary"
            >
              Limpiar filtros
            </button>
            <button
              type="button"
              onClick={() => {
                onApply(draft);
                onClose();
              }}
              className="inline-flex min-h-11 items-center justify-center rounded-button bg-accent px-4 py-2 font-heading text-sm font-semibold text-bg-primary"
            >
              Ver resultados
            </button>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}

export function ProfilesDirectoryControls({ categories, filters, locations }: ProfilesDirectoryControlsProps) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const categoryOptions = useMemo(
    () => [
      { label: 'Categoria', value: '' },
      ...categories.map((category) => ({ label: category.name, value: category.slug })),
    ],
    [categories]
  );
  const selectedCategory = useMemo(
    () => categories.find((category) => category.slug === filters.category),
    [categories, filters.category]
  );
  const typeOptions = useMemo(
    () => [
      { label: 'Tipo de negocio', value: '' },
      ...(selectedCategory?.types ?? []).map((type) => ({
        label: type.name,
        value: type.slug,
      })),
    ],
    [selectedCategory]
  );
  const locationOptions = useMemo(
    () => [{ label: 'Ubicacion', value: '' }, ...locations.map((location) => ({ label: location, value: location }))],
    [locations]
  );

  const navigate = (updates: Partial<DirectoryControlFilters>) => {
    startTransition(() => {
      router.push(buildFilterHref(filters, updates));
    });
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    navigate({ query: inputRef.current?.value ?? '' });
  };

  return (
    <>
      <PendingIndicator isPending={isPending} />
      <form onSubmit={handleSubmit} className="mt-7 max-w-5xl">
        <label
          htmlFor="profiles-directory-search"
          className="flex min-h-14 cursor-text items-center gap-3 rounded-full border border-accent/50 bg-bg-secondary/95 px-4 py-2 text-text-primary shadow-[0_18px_55px_rgb(0_0_0/0.36)] transition-colors focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/30"
        >
          <Search size={22} className="shrink-0 text-accent" />
          <input
            ref={inputRef}
            id="profiles-directory-search"
            type="search"
            defaultValue={filters.query}
            placeholder="Busca una actividad, negocio o ubicacion"
            className="h-10 min-w-0 flex-1 cursor-text bg-transparent text-sm outline-none placeholder:text-text-secondary focus-visible:ring-opacity-0 focus-visible:ring-offset-0"
          />
          <button
            type="submit"
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-accent text-bg-primary transition-colors hover:bg-accent-hover"
            aria-label="Buscar"
          >
            <Search size={20} />
          </button>
        </label>

        <div className="mt-4 grid gap-3 md:flex md:flex-wrap">
          <div className="grid gap-3 md:flex md:flex-wrap">
            <div className="md:contents">
              <DropdownFilter
                icon={<Grid2X2 size={15} />}
                label="Categoria"
                value={filters.category}
                options={categoryOptions}
                onChange={(category) => {
                  if (category) {
                    trackEvent('directory_category_filter_applied', {
                      category_slug: category,
                      source: 'profiles_directory',
                    });
                  }
                  navigate({ category, type: '' });
                }}
              />
              {selectedCategory && typeOptions.length > 1 ? (
                <DropdownFilter
                  icon={<Grid2X2 size={15} />}
                  label="Tipo de negocio"
                  value={filters.type}
                  options={typeOptions}
                  onChange={(type) => {
                    if (type) {
                      trackEvent('directory_business_type_filter_applied', {
                        business_type_slug: type,
                        category_slug: filters.category,
                        source: 'profiles_directory',
                      });
                    }
                    navigate({ type });
                  }}
                />
              ) : null}
            </div>
            <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 md:contents">
              <DropdownFilter
                icon={<MapPin size={15} />}
                label="Ubicacion"
                value={filters.location}
                options={locationOptions}
                onChange={(location) => navigate({ location })}
              />
              <button
                type="button"
                onClick={() => setIsMobileFiltersOpen(true)}
                className={cn(
                  'inline-flex min-h-10 items-center justify-center gap-2 rounded-button border px-4 py-2 text-sm font-semibold transition-colors md:hidden',
                  hasActiveMobileFilters(filters)
                    ? 'border-accent bg-accent text-bg-primary'
                    : 'border-border-strong bg-bg-secondary/90 text-text-primary'
                )}
              >
                <SlidersHorizontal size={15} />
                Filtros
              </button>
            </div>
          </div>

          <div className="hidden flex-wrap gap-3 md:flex">
            <FilterButton active={filters.openToday} filters={filters} updates={{ openToday: !filters.openToday }}>
            <CalendarDays size={15} />
            Disponibilidad hoy
          </FilterButton>
            <FilterButton active={filters.whatsapp} filters={filters} updates={{ whatsapp: !filters.whatsapp }}>
              <MessageCircle size={15} />
              Contacto por WhatsApp
            </FilterButton>
            <FilterButton active={filters.rating} filters={filters} updates={{ rating: !filters.rating }}>
              <Star size={15} />
              Mejor valorados
            </FilterButton>
          </div>
        </div>
      </form>

      <MobileFilterSheet
        filters={filters}
        isOpen={isMobileFiltersOpen}
        locations={locations}
        onApply={navigate}
        onClose={() => setIsMobileFiltersOpen(false)}
      />
    </>
  );
}

export function DirectorySortControl({ filters }: { filters: DirectoryControlFilters }) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const sortOptions = [
    { label: 'Mas recomendados', value: 'recomendados' },
    { label: 'Mejor calificados', value: 'valorados' },
    { label: 'Nombre', value: 'nombre' },
  ];

  return (
    <>
      <PendingIndicator isPending={isPending} />
      <div className="w-full sm:w-auto">
        <DropdownFilter
          icon={<span className="text-text-secondary">Ordenar</span>}
          label="Ordenar"
          value={filters.sort}
          options={sortOptions}
          onChange={(sort) => {
            startTransition(() => {
              router.push(buildFilterHref(filters, { sort }));
            });
          }}
        />
      </div>
    </>
  );
}
